import { useState, useRef, useEffect } from 'react'
import { Button } from '../common/Button'
import { Card } from '../common/Card'
import type { Challenge, ChallengeAnswer } from '../../types'

interface PhotoCaptureProps {
  challenge: Challenge
  onSubmit: (answer: ChallengeAnswer) => void
  isSubmitting: boolean
}

export function PhotoCapture({
  challenge,
  onSubmit,
  isSubmitting,
}: PhotoCaptureProps) {
  const [mode, setMode] = useState<'camera' | 'preview'>('camera')
  const [photo, setPhoto] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [cameraReady, setCameraReady] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  useEffect(() => {
    startCamera()
    return () => stopCamera()
  }, [])

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.onloadedmetadata = () => {
          setCameraReady(true)
        }
      }
    } catch (err) {
      console.error('Camera error:', err)
      setError('Camera permission denied. Please allow camera access.')
    }
  }

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current
      const canvas = canvasRef.current
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const context = canvas.getContext('2d')
      if (context) {
        context.drawImage(video, 0, 0)
        const imageData = canvas.toDataURL('image/jpeg', 0.8)
        setPhoto(imageData)
        setMode('preview')
        stopCamera()
      }
    }
  }

  const retakePhoto = () => {
    setPhoto(null)
    setMode('camera')
    startCamera()
  }

  const handleSubmit = () => {
    if (photo) {
      // Strip the data URL prefix to get base64
      const base64 = photo.split(',')[1]
      onSubmit({ photo_data: base64 })
    }
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Card variant="parchment" padding="md">
          <p className="text-error-600">{error}</p>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Card variant="parchment" padding="md">
        <p className="text-brown-800">
          {challenge.photo_hint || challenge.instructions || 'Take a photo to complete this challenge'}
        </p>
      </Card>

      {mode === 'camera' && (
        <>
          <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-black">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            {!cameraReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-cream-100">
                <div className="text-center text-brown-500">
                  <svg
                    className="w-12 h-12 mx-auto mb-2 animate-pulse"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <p>Starting camera...</p>
                </div>
              </div>
            )}
          </div>

          <Button
            onClick={capturePhoto}
            disabled={!cameraReady}
            className="w-full"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            Take Photo
          </Button>
        </>
      )}

      {mode === 'preview' && photo && (
        <>
          <div className="relative aspect-[4/3] rounded-lg overflow-hidden">
            <img
              src={photo}
              alt="Captured"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={retakePhoto}
              disabled={isSubmitting}
              className="flex-1"
            >
              Retake
            </Button>
            <Button
              onClick={handleSubmit}
              loading={isSubmitting}
              className="flex-1"
            >
              Submit Photo
            </Button>
          </div>
        </>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  )
}
